import { useState } from 'react';
import type { NominationAdvice, NominationOption } from '../lib/aiAdvisor';
import { TeamLogo } from './TeamLogo';
import { sounds } from '../lib/soundEffects';

interface Props {
  advice: NominationAdvice;
  onSelectNomination: (team: string, bid: number) => void;
  onOpenWarRoom?: () => void;
}

export function AINominationAdvisor({ advice, onSelectNomination, onOpenWarRoom }: Props) {
  const [selectedOption, setSelectedOption] = useState<NominationOption>(advice.primary);

  const handleSelect = (opt: NominationOption) => {
    sounds.playYourTurn();
    onSelectNomination(opt.team, opt.recommendedBid);
  };

  const getStrategyBadge = (strategy: NominationOption['strategy']) => {
    switch (strategy) {
      case 'DRAIN_RIVALS':
        return 'bg-rose-500/90 text-white';
      case 'VALUE_TARGET':
        return 'bg-emerald-500/90 text-slate-950';
      case 'CAP_SQUEEZE':
        return 'bg-amber-500/90 text-slate-950';
      case 'POWER_ANCHOR':
        return 'bg-cyan-500/90 text-slate-950';
      case 'BARGAIN_SLEEPER':
      default:
        return 'bg-purple-500/90 text-white';
    }
  };

  const allOptions = [advice.primary, ...advice.alternatives];

  return (
    <div className="panel p-4 border-2 border-cyan-500/80 bg-gradient-to-b from-slate-900 to-slate-950 mb-4 shadow-xl shadow-cyan-950/20">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-xl">🤖</span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-extrabold text-cyan-400">
                Rob's AI Nomination Advisor
              </span>
              <span className="pill bg-cyan-950 border border-cyan-500/50 text-cyan-300 text-[9px] font-mono">
                LIVE ENGINE
              </span>
            </div>
            <h3 className="text-base font-extrabold text-white">
              Recommended Team to Nominate
            </h3>
          </div>
        </div>

        {onOpenWarRoom && (
          <button
            onClick={onOpenWarRoom}
            className="text-xs px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-cyan-500/40"
          >
            🧠 War Room
          </button>
        )}
      </div>

      {/* Strategy selector pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3">
        {allOptions.map((opt, i) => {
          const isSelected = selectedOption.team === opt.team;
          return (
            <button
              key={opt.team + i}
              onClick={() => setSelectedOption(opt)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition border ${
                isSelected
                  ? 'bg-cyan-500 text-slate-950 border-cyan-400 shadow'
                  : 'bg-slate-800/80 text-slate-300 border-slate-700 hover:bg-slate-700'
              }`}
            >
              <span>{opt.strategyTitle.split(' ')[0]}</span>
              <span>{opt.team}</span>
              <span className="text-[10px] opacity-80">(${opt.recommendedBid})</span>
            </button>
          );
        })}
      </div>

      {/* Selected Recommendation Spotlight */}
      <div className="bg-slate-800/60 rounded-xl p-3.5 border border-slate-700 mb-3">
        <div className="flex items-start gap-3 mb-3">
          <TeamLogo abbr={selectedOption.team} size={52} className="shrink-0" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${getStrategyBadge(selectedOption.strategy)}`}>
                {selectedOption.strategyTitle}
              </span>
              <span className="text-xs text-slate-400">
                Proj: <strong className="text-white">{selectedOption.projectedWins.toFixed(1)}W</strong>
              </span>
              <span className="text-xs text-slate-400">
                Fair Value: <strong className="text-teal-300">${selectedOption.fairValue}</strong>
              </span>
            </div>
            <h4 className="text-lg font-black text-white leading-tight">
              {selectedOption.teamName} ({selectedOption.team})
            </h4>
            <div className="text-xs font-semibold text-cyan-300 mt-0.5">
              Opening Bid: <span className="text-emerald-400 font-extrabold">${selectedOption.recommendedBid}</span>
            </div>
          </div>
        </div>

        {/* Tactical Rationale */}
        <p className="text-xs text-slate-300 leading-relaxed bg-black/30 p-2.5 rounded-lg border border-white/5 mb-3">
          💡 <strong>Why This Pick:</strong> {selectedOption.tacticalRationale}
        </p>

        {/* 1-Click Action to Nominate */}
        <button
          onClick={() => handleSelect(selectedOption)}
          className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-400 hover:from-cyan-400 hover:to-teal-300 text-slate-950 font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 active:scale-[0.99] transition"
        >
          <span>🎯 Nominate {selectedOption.team} @ ${selectedOption.recommendedBid}</span>
        </button>
      </div>

      <div className="text-[11px] text-slate-400 text-center">
        {advice.marketContext}
      </div>
    </div>
  );
}
